import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from '../context/shared/AuthContext';
import { useTheme } from '../context/shared/ThemeContext';
import { Patient, ReportDataPayload } from '../shared/types/src';
import PageContainer from '../components/common/PageContainer';
import PageHeader from '../components/common/PageHeader';
import PageSection from '../components/common/PageSection';
import apiService from '../services/shared/apiService';
import ReportPreview from '../components/therapist/ReportPreview';
import { jsPDF } from 'jspdf';
import JSZip from 'jszip';
import ScheduleReportModal from '../components/therapist/ScheduleReportModal';

const ReportsPage: React.FC = () => {
    const { user } = useAuth();
    const { themeClasses } = useTheme();
    const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
    const [patients, setPatients] = useState<Patient[]>([]);
    const [startDate, setStartDate] = useState<Date>(new Date(new Date().setDate(new Date().getDate() - 30)));
    const [endDate, setEndDate] = useState<Date>(new Date());
    const [reportData, setReportData] = useState<ReportDataPayload | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isScheduling, setIsScheduling] = useState(false);

    useEffect(() => {
        const fetchPatients = async () => {
            if (user?.uid) {
                try {
                    const response = await apiService.getTherapistPatients(user.uid);
                    setPatients(response.patients);
                } catch (error) {
                    console.error("Failed to fetch patients:", error);
                }
            }
        };
        fetchPatients();
    }, [user]);

    const handlePatientSelection = (patientId: string) => {
        setSelectedPatients(prev =>
            prev.includes(patientId)
                ? prev.filter(id => id !== patientId)
                : [...prev, patientId]
        );
    };

    const handleGenerateReport = async () => {
        if (selectedPatients.length !== 1 || !user?.uid) {
            alert("Please select a single patient to generate a preview.");
            return;
        }
        setIsGenerating(true);
        setReportData(null);
        try {
            const data = await apiService.getReportData(user.uid, selectedPatients[0], startDate.toISOString(), endDate.toISOString());
            setReportData(data);
        } catch (error) {
            console.error("Failed to generate report:", error);
            alert("Failed to generate report. Please check the console for details.");
        }
        setIsGenerating(false);
    };

    const handleDownloadAll = async () => {
        if (selectedPatients.length === 0 || !user?.uid) {
            alert("Please select at least one patient.");
            return;
        }
        setIsGenerating(true);
        const zip = new JSZip();

        for (const patientId of selectedPatients) {
            try {
                const data = await apiService.getReportData(user.uid, patientId, startDate.toISOString(), endDate.toISOString());
                const pdf = new jsPDF();
                // This is a placeholder for actual PDF generation logic
                pdf.text(`Report for ${data.patientInfo.displayName}`, 10, 10);
                pdf.text(`Date Range: ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`, 10, 20);
                pdf.text(`Total Alerts: ${data.alertSummary.totalAlerts}`, 10, 30);
                const pdfBlob = pdf.output('blob');
                zip.file(`Report_${data.patientInfo.displayName.replace(/\s/g, '_')}_${endDate.toLocaleDateString()}.pdf`, pdfBlob);
            } catch (error) {
                console.error(`Failed to generate report for patient ${patientId}:`, error);
            }
        }

        zip.generateAsync({ type: 'blob' }).then(content => {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(content);
            link.download = `Mentra_Reports_${new Date().toLocaleDateString()}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        });

        setIsGenerating(false);
    };

    return (
        <PageContainer>
            <PageHeader 
                title="Clinical Reports" 
                emoji="📄"
                description="Generate comprehensive reports for your patients"
            />
            
            {/* Warning Banner */}
            <PageSection title="Development Notice" emoji="⚠️">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                        <strong>Warning:</strong> The reports generated here are for development and demonstration purposes only. They are not yet suitable for real clinical use.
                    </p>
                </div>
            </PageSection>

            {/* Patient Selection */}
            <PageSection title="Select Patients" emoji="👥">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Choose Patients
                        </label>
                        <select
                            multiple
                            value={selectedPatients}
                            onChange={(e) => {
                                const values = Array.from(e.target.selectedOptions, option => option.value);
                                setSelectedPatients(values);
                            }}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 min-h-32"
                        >
                            {patients.map(p => (
                                <option key={p.uid} value={p.uid}>
                                    {p.displayName}
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            Hold Ctrl/Cmd to select multiple patients
                        </p>
                    </div>
                </div>
            </PageSection>

            {/* Date Range Selection */}
            <PageSection title="Select Date Range" emoji="📅">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                            Start Date
                        </label>
                        <DatePicker
                            selected={startDate}
                            onChange={(date: Date | null) => date && setStartDate(date)}
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                            End Date
                        </label>
                        <DatePicker
                            selected={endDate}
                            onChange={(date: Date | null) => date && setEndDate(date)}
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>
            </PageSection>

            {/* Actions */}
            <PageSection title="Generate Reports" emoji="⚡">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                        onClick={handleGenerateReport}
                        disabled={isGenerating || selectedPatients.length !== 1}
                        className="p-4 bg-blue-100 hover:bg-blue-200 rounded-lg text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">👁️</span>
                            <div>
                                <div className="font-medium" style={{color: '#000000'}}>
                                    {isGenerating ? 'Generating...' : 'Preview Single Report'}
                                </div>
                                <div className="text-sm" style={{color: '#374151'}}>Generate and preview one report</div>
                            </div>
                        </div>
                    </button>
                    <button
                        onClick={handleDownloadAll}
                        disabled={isGenerating || selectedPatients.length === 0}
                        className="p-4 bg-green-100 hover:bg-green-200 rounded-lg text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">📥</span>
                            <div>
                                <div className="font-medium" style={{color: '#000000'}}>
                                    {isGenerating ? 'Generating...' : `Download ${selectedPatients.length} Report(s)`}
                                </div>
                                <div className="text-sm" style={{color: '#374151'}}>Download all selected reports</div>
                            </div>
                        </div>
                    </button>
                    <button
                        onClick={() => setIsScheduling(true)}
                        disabled={isGenerating || selectedPatients.length === 0}
                        className="p-4 bg-purple-100 hover:bg-purple-200 rounded-lg text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">⏰</span>
                            <div>
                                <div className="font-medium" style={{color: '#000000'}}>Schedule Reports</div>
                                <div className="text-sm" style={{color: '#374151'}}>Set up automated reports</div>
                            </div>
                        </div>
                    </button>
                </div>
            </PageSection>

            {isGenerating && <PageSection title="Generating..." emoji="⏳"><div className="text-center p-8">Loading report data...</div></PageSection>}

            {reportData && (
                <PageSection title="Report Preview" emoji="👀">
                    <ReportPreview data={reportData} />
                </PageSection>
            )}

            <ScheduleReportModal
                isOpen={isScheduling}
                onClose={() => setIsScheduling(false)}
                patients={patients}
                selectedPatientIds={selectedPatients}
            />
        </PageContainer>
    );
};

export default ReportsPage;